import { type FC, useMemo, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useContentQuery,
  useContentTimecodesQuery,
  useUpdateContentMutation,
  useContentStatusChangedSubscription,
  useContentStatusQuery,
} from "@/partner/entities/contents";
import {
  ComplianceScreen,
  ComplianceWarning,
  useComplianceEnabled,
} from "@/partner/features/compliance";
import { VideoPollsScreen } from "@/partner/features/polls";
import { usePlatformFeature } from "@/partner/entities/platforms";
import { PlatformFeatureType } from "@/types/graphql/partner.generated";
import { mapVideoToFormValues, type VideoFormValues } from "../model";
import { VideoForm } from "./VideoForm";
import { VideoTabsMenu } from "./VideoTabsMenu";
import { TimecodesScreen } from "./timecodes";

interface EditVideoDialogProps {
  open: boolean;
  contentId: string | null;
  /** Активная вкладка формы (?tab=): "edit" | "timecodes" | "records2257" | "polls"
   * (референсный VideoForm/Menu плюс записи 2257 фазы 4). */
  tab?: string;
  onClose: () => void;
  onCompleted?: () => void;
}

export const EditVideoDialog: FC<EditVideoDialogProps> = ({
  open,
  contentId,
  tab = "edit",
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // Поднято из VideoForm/VideoUploadField — нужно и для disabled на submit
  // (внутри VideoForm), и здесь для гейта закрытия формы во время загрузки.
  const [isUploading, setIsUploading] = useState(false);
  // Актуальный abort() из useVideoUpload (см. VideoUploadField) — вызывается
  // при подтверждённом закрытии, чтобы реально оборвать tus-загрузку, а не
  // только заблокировать сам onClose (см. референсный VideoForm.js:253-262:
  // onCloseDialog вызывает cancelUpload() перед onClose()).
  const abortUploadRef = useRef<(() => void) | null>(null);

  const handleUploadingChange = (
    uploading: boolean,
    abortUpload: () => void
  ) => {
    setIsUploading(uploading);
    abortUploadRef.current = abortUpload;
  };

  const handleClose = () => {
    // Клон референсного contentDialog.form.confirmCancelUpload
    // (VideoForm.js:255) — window.confirm перед закрытием формы, пока
    // загрузка видео ещё активна.
    if (isUploading) {
      if (!window.confirm(t("videos.edit.confirmCancelUpload"))) {
        return;
      }
      abortUploadRef.current?.();
    }
    onClose();
  };

  const { data, loading } = useContentQuery({
    variables: { id: String(contentId) },
    skip: !contentId || !open,
  });

  const isProcessing = data?.content?.processingStatus === "PROCESSING";

  // Живой статус: подписка + поллинг-фолбэк, пока PROCESSING
  useContentStatusChangedSubscription({
    variables: { id: contentId ?? "" },
    skip: !contentId || !isProcessing,
  });
  useContentStatusQuery({
    variables: { id: contentId ?? "" },
    skip: !contentId || !isProcessing,
    pollInterval: 5000,
  });

  const [updateContent, { loading: updating }] = useUpdateContentMutation({
    onCompleted: () => {
      enqueueSnackbar(t("videos.edit.success"), { variant: "success" });
      onCompleted?.();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["ContentsList"],
  });

  const initialValues = useMemo(
    () => (data ? mapVideoToFormValues(data.content) : undefined),
    [data]
  );

  // Сохранённые метки таймлайна — видны в превью-плеере вкладки
  // редактирования (в TimecodesScreen — свой локальный стейт с правками).
  const { data: timecodesData } = useContentTimecodesQuery({
    variables: { id: String(contentId) },
    skip: !contentId || !open || tab !== "edit",
  });

  const previewTimecodes = useMemo(
    () =>
      (timecodesData?.contentTimecodes?.timecodes ?? []).map((timecode) => ({
        id: String(timecode.id),
        type: timecode.type,
        time: timecode.time,
        title: timecode.title,
        description: timecode.description,
        icon: timecode.icon,
        duration: timecode.duration,
        position: timecode.position,
      })),
    [timecodesData?.contentTimecodes?.timecodes]
  );

  const handleSubmit = async (values: VideoFormValues) => {
    await updateContent({
      variables: {
        id: String(contentId),
        input: values,
      },
    });
  };

  const complianceEnabled = useComplianceEnabled();
  const pollsEnabled = usePlatformFeature(PlatformFeatureType.PlayerPollsType);

  // Вкладки как в референсном VideoForm/Menu (edit/captions/timecodes;
  // субтитры — в бэклоге). Активная вкладка живёт в ?tab=. Предупреждение
  // о пустых записях 2257 само возвращает null, когда комплаенс выключен,
  // тайтл не опубликован или записи уже есть.
  const menu = contentId ? (
    <>
      <VideoTabsMenu tab={tab} />
      <ComplianceWarning
        contentId={contentId}
        published={Boolean(data?.content?.publishAt)}
      />
    </>
  ) : undefined;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      {complianceEnabled && tab === "records2257" && contentId ? (
        <ComplianceScreen
          contentId={contentId}
          onClose={handleClose}
          menu={menu}
        />
      ) : pollsEnabled && tab === "polls" && contentId ? (
        <VideoPollsScreen
          contentId={contentId}
          onClose={handleClose}
          menu={menu}
        />
      ) : tab === "timecodes" && contentId ? (
        <TimecodesScreen
          contentId={contentId}
          onClose={handleClose}
          videoDuration={
            data?.content?.video?.duration ?? data?.content?.duration ?? 0
          }
          hlsUrl={data?.content?.playback?.hlsUrl}
          thumbnailUrl={data?.content?.playback?.thumbnailUrl}
          menu={menu}
        />
      ) : (
        <VideoForm
          title={t("videos.edit.title")}
          loading={loading || updating}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          onUploadingChange={handleUploadingChange}
          // Живой статус (PROCESSING → COMPLETE) пересчитывает initialValues
          // из подписки/поллинга — не должно затирать уже введённые
          // пользователем несохранённые правки формы.
          keepDirtyOnReinitialize
          menu={menu}
          timecodes={previewTimecodes}
          metadata={{
            contentId,
            url: data?.content?.url,
            embedUrl: data?.content?.embedUrl,
            hlsUrl: data?.content?.playback?.hlsUrl,
            processingStatus: data?.content?.processingStatus,
            thumbnailUrl: data?.content?.playback?.thumbnailUrl,
            transcodingError: data?.content?.transcodingError,
          }}
        />
      )}
    </Dialog>
  );
};
